<template>
    <div
        class="relative flex w-full flex-grow flex-col border border-gray-100 bg-white md:shadow-sm"
        :class="{
            'rounded-full': roundedFull && !isMultiline,
            'rounded-3xl': roundedFull && isMultiline,
            'rounded-md md:rounded-2xl': !roundedFull,
            'border-red-500': error,
            'border-stone-300 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary': !error,
        }"
    >
        <!--
            CLIP-39. The field grows with what is typed, up to about five lines,
            then scrolls. Enter still submits; Shift+Enter breaks the line. While
            it is one line tall nothing here renders differently from before: the
            pill shape, the centred controls and the padding are the same classes.
            Only once a second line appears does the pill soften to rounded-3xl and
            do the controls drop to the bottom edge, so they stay next to the line
            being typed instead of floating mid-height.
        -->
        <div class="flex divide-x px-2" :class="isMultiline ? 'items-end' : 'items-center'">
            <textarea
                ref="textarea"
                tabindex="0"
                rows="1"
                v-model="model"
                @input="resize"
                @keydown.enter.exact.prevent="$emit('submit')"
                :disabled="disabled"
                class="m-0 w-full cursor-text resize-none appearance-none overflow-x-hidden rounded-none border-0 border-solid border-zinc-400 bg-transparent py-2 pr-1 text-sm focus:border-none focus:ring-0 md:py-3 md:text-base"
                :class="{
                    'opacity-50': disabled,
                    'overflow-y-auto': atMaxHeight,
                    'overflow-y-hidden': !atMaxHeight,
                }"
                :placeholder="placeholder"
            ></textarea>

            <!--
                Margin, not padding, for the multi-line offset: the divide-x rule
                is this wrapper's left border, and padding would stretch that line
                down to the pill's bottom edge. The values centre the filter
                button on the last text line, exactly as the submit button below.
                Three steps because two breakpoints are in play: the button grows
                from 16px to 24px at sm (its sm:leading-6), the textarea padding
                from 8px to 12px only at md. Mobile 8 + (20-16)/2 = 10; sm band
                8 + (20-24)/2 = 6; md 12 + (24-24)/2 = 12.
            -->
            <div class="pl-3" :class="{ 'mb-2.5 sm:mb-1.5 md:mb-3': isMultiline }">
                <YearRangeSelector />
            </div>

            <button
                type="submit"
                title="Submit question"
                :class="{
                    'bg-gray-200 text-gray-500': disabled,
                    'bg-primary-50 text-primary-600': !disabled,
                    'mb-1 md:mb-1.5': isMultiline,
                }"
                class="inline-flex size-7 shrink-0 items-center justify-center rounded-full border-0 p-1 hover:bg-primary-600 hover:text-white focus:bg-primary-600 focus:text-white active:bg-primary-500/25 md:size-8 md:h-9 md:w-9"
            >
                <PaperAirplaneIcon v-if="!disabled" class="size-4 md:size-5" />
                <ArrowPathIcon v-else class="size-4 animate-spin md:size-5" />
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch, type ModelRef } from 'vue'
import { ArrowPathIcon, PaperAirplaneIcon } from '@heroicons/vue/24/outline'
import YearRangeSelector from '@/Components/YearRangeSelector.vue'

const model: ModelRef<string> = defineModel({ default: '' })

withDefaults(
    defineProps<{
        roundedFull?: boolean
        error?: boolean | undefined
        disabled?: boolean | undefined
        placeholder?: string | undefined
    }>(),
    {
        roundedFull: false,
        error: false,
        disabled: false,
        placeholder: '',
    }
)

defineEmits(['submit'])

/** The field grows to this many lines, then scrolls. */
const MAX_LINES = 5

const textarea = ref<HTMLTextAreaElement | null>(null)
const isMultiline = ref(false)
const atMaxHeight = ref(false)

/**
 * Line height and single-line height from the computed styles rather than from
 * a measurement of the empty field: the chat footer can mount it with text
 * already in it, and a measurement taken then would call two lines "one". The
 * cap is derived the same way so it always lands on a whole line at every
 * breakpoint, instead of a fixed pixel count that cut a line in half on desktop
 * and allowed eight lines on mobile.
 */
const metrics = (el: HTMLTextAreaElement): { single: number; max: number } => {
    const style = getComputedStyle(el)
    const line = parseFloat(style.lineHeight)
    const single = line + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)

    return { single, max: single + (MAX_LINES - 1) * line }
}

const resize = () => {
    const el = textarea.value
    if (!el) return

    // An empty field is one line by definition. scrollHeight would otherwise
    // measure the PLACEHOLDER, which wraps to two or three lines at phone
    // widths for a long podcaster name, and the empty field would come back
    // from a submit tall and multi-line-shaped.
    if (el.value === '') {
        el.style.height = ''
        atMaxHeight.value = false
        isMultiline.value = false
        return
    }

    const { single, max } = metrics(el)

    // Collapse first so scrollHeight reports the content, not the previous height.
    el.style.height = 'auto'
    const natural = el.scrollHeight

    el.style.height = `${Math.min(natural, max)}px`
    atMaxHeight.value = natural > max
    // A couple of pixels of slack so sub-pixel rounding never flips a single line to "multi".
    isMultiline.value = natural > single + 2
}

// Covers the parent clearing the field after a submit, and any programmatic fill.
watch(model, () => nextTick(resize))

// A width change reflows the text (rotating a phone, crossing a breakpoint), and
// a height fixed from the old width would clip lines with no way to scroll them.
onMounted(() => {
    nextTick(resize)
    window.addEventListener('resize', resize)
})
onBeforeUnmount(() => window.removeEventListener('resize', resize))
</script>

<style scoped>
/* The placeholder stays a single clipped line, as it was before the field could
   wrap. Letting it wrap showed a half-cut second line inside the one-line field
   on narrow screens. */
textarea::placeholder {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
